import React, { useCallback, useRef, useEffect, memo, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { MessageSquare, FileText } from 'lucide-react';
import ChatInput from './ChatInput';
import { MessageList } from './Messages';
import ScrollableChatContainer from './ScrollableChatContainer';
import SalesReportButton from './SalesReportButton';
import { useTranslation } from '../../../i18n';

const ChatPanel = memo(({
  messages,
  isLoading,
  handleSubmit,
  darkMode,
  chatContainerRef,
  setShowPromptStructure,
  onShowCarDetail,
  fileUploadProgress,
}) => {
  const { t } = useTranslation('assistant');
  const [showRawMessages, setShowRawMessages] = useState(false);
  const [generatedChartData, setGeneratedChartData] = useState(null);

  const toolQuickActions = useMemo(() => ([
    {
      id: 'knowledge_first',
      label: '1. Knowledge',
      prompt: "Parti dalla knowledge base: usa knowledge_search_engine per trovare procedure, policy e informazioni ufficiali utili alla richiesta. Poi dammi una sintesi pratica in 5 punti.",
      description: 'Verifica prima la base documentale aziendale.',
      mode: 'send',
    },
    {
      id: 'data_analysis',
      label: '2. Analisi Dati',
      prompt: "Dopo la verifica knowledge, usa customer_chat_analytics per analizzare i dati più rilevanti (lead, conversioni, performance chat e costi) e proponi 3 azioni operative.",
      description: 'Analizza KPI e dati CRM in modo operativo.',
      mode: 'send',
    },
    {
      id: 'catalog_configurator',
      label: '3. Catalogo',
      prompt: "Dopo knowledge e analisi dati, usa car_configurator per proporre allestimenti da catalogo (Eurotax) con confronto sintetico: versione, prezzo, alimentazione e next step commerciale.",
      description: 'Usa il catalogo nuovo dopo il contesto business.',
      mode: 'send',
    },
    {
      id: 'skill_playbook',
      label: 'Skill',
      prompt: "Mostrami le skill disponibili con dealership_skill_catalog e applica quella più adatta con dealership_skill_apply per questa richiesta.",
      description: 'Attiva playbook operativi predefiniti della concessionaria.',
      mode: 'send',
    },
  ]), []);

  const createSyntheticChartData = () => {
    const salesMessage = messages.find(msg => 
      msg.sender === 'server' && 
      (msg.text.includes('vendite') || 
       msg.text.includes('venduto') || 
       msg.text.toLowerCase().includes('analisi'))
    );
    
    if (!salesMessage) {
      alert(t('chatPanel.noSalesAnalysisAlert'));
      return;
    }
    
    const salesText = salesMessage.text;
    
    const carsMatch = salesText.match(/(?:Abbiamo\s+venduto|venduto)\s+(\d+)\s+auto/i);
    const totalCarsMatch = salesText.match(/su\s+un\s+totale\s+di\s+(\d+)/i);
    const remainingMatch = salesText.match(/(\d+)\s+ancora\s+disponibili/i);
    
    const brandMatches = [...salesText.matchAll(/([A-Za-z]+)\s+\((\d+)(?:\s+vendute)?\)/g)];
    const brands = brandMatches.map(match => ({ brand: match[1], count: parseInt(match[2]) }));
    
    const modelRegex = /([\w\s-]+)\s+\((\d+)\)/g;
    const modelSection = salesText.split('I modelli top sono stati:')[1]?.split('.')[0];
    const modelMatches = modelSection ? [...modelSection.matchAll(modelRegex)] : [];
    const models = modelMatches.map(match => ({ model: match[1].trim(), count: parseInt(match[2]) }));
    
    const bodyRegex = /(SUV|berlina|wagon|coupé|city car)\s+\((\d+)\)/g;
    const bodyMatches = [...salesText.matchAll(bodyRegex)];
    const bodyTypes = bodyMatches.map(match => ({ type: match[1], count: parseInt(match[2]) }));
    
    const soldCars = carsMatch ? parseInt(carsMatch[1]) : 0;
    const totalCars = totalCarsMatch ? parseInt(totalCarsMatch[1]) : 0;
    const remainingCars = remainingMatch ? parseInt(remainingMatch[1]) : 0;
    
    const syntheticData = {
      descriptive_information: t('chatPanel.chart.description', { defaultValue: 'Sales insights analysis' }),
      data: {
        sold: soldCars,
        total: totalCars,
        remaining: remainingCars,
        brands: brands,
        models: models,
        bodyTypes: bodyTypes
      },
      chart_type: {
        market_overview: true,
        transaction_analysis: true
      },
      chart_data: {
        distribution: {
          title: t('chatPanel.chart.distributionTitle', { defaultValue: 'Sales distribution by brand' }),
          labels: brands.map(b => b.brand),
          data: brands.map(b => b.count)
        },
        overall_body_styles: {
          title: t('chatPanel.chart.bodyStylesTitle', { defaultValue: 'Sales by body type' }),
          labels: bodyTypes.map(b => b.type),
          data: bodyTypes.map(b => b.count)
        },
        sales_status: {
          title: t('chatPanel.chart.statusTitle', { defaultValue: 'Sales status' }),
          labels: [
            t('chatPanel.chart.salesStatus.labels.sold', { defaultValue: 'Sold' }),
            t('chatPanel.chart.salesStatus.labels.available', { defaultValue: 'Available' })
          ],
          data: [soldCars, remainingCars]
        }
      },
      analysis_metadata: {
        total_cars_analyzed: totalCars,
        analysis_type: "transaction_analysis",
        date_range: salesText.match(/per il mese di ([A-Za-z]+)/i)?.[1] || t('chatPanel.chart.defaultDateRange', { defaultValue: 'Last period' })
      }
    };
    
    try {
      salesMessage.carInsightsData = syntheticData;
      
      if (!salesMessage.tool) {
        salesMessage.tool = ['car_insights_analysis'];
      } else if (Array.isArray(salesMessage.tool) && !salesMessage.tool.includes('car_insights_analysis')) {
        salesMessage.tool.push('car_insights_analysis');
      }
      
      setGeneratedChartData(syntheticData);
      
      setShowRawMessages(prev => !prev);
      setTimeout(() => setShowRawMessages(prev => !prev), 100);
    } catch (error) {
      console.error('Error generating chart data:', error);
      alert(t('chatPanel.chart.errorAlert', { defaultValue: 'Error generating chart data. See console for details.' }));
    }
  };

  // Clean message text function with useCallback
  const cleanMessageText = useCallback((text) => {
    if (!text) return '';
    
    // Remove or skip any Car Insights Visualization Guide documentation text
    if (text.includes('# Car Insights Visualization Guide')) {
      return t('chatPanel.insightsGuideReminder', { defaultValue: 'Welcome back to the Assistant. How can I help you today?' });
    }

    // First check if the message contains car insights data in JSON format
    let carInsightsMatch = text.match(/```json\s*(\{[\s\S]*?\})\s*```/);
    
    // If not found with explicit json tag, try to look for any JSON object
    if (!carInsightsMatch) {
      carInsightsMatch = text.match(/```\s*(\{[\s\S]*?\})\s*```/);
    }
    
    // If still not found, look for JSON outside of code blocks
    if (!carInsightsMatch) {
      const jsonRegex = /\{[\s\S]*?"descriptive_information"[\s\S]*?"chart_data"[\s\S]*?\}/;
      const match = text.match(jsonRegex);
      if (match) {
        carInsightsMatch = [match[0], match[0]];
      }
    }
    
    if (carInsightsMatch) {
      try {
        // Parse the JSON data
        const insightsData = JSON.parse(carInsightsMatch[1]);
        
        // Check if it has the expected structure for car insights
        if (insightsData.descriptive_information && insightsData.chart_data) {
          // Create example data for testing if chart_data is empty
          if (!insightsData.chart_data || Object.keys(insightsData.chart_data).length === 0) {
            insightsData.chart_data = {
              distribution: {
                title: t('chatPanel.chart.distributionTitle', { defaultValue: 'Sales distribution by brand' }),
                labels: ['Volkswagen', 'Audi', 'Skoda', 'Porsche', 'Citroen', 'Ford'],
                data: [21, 18, 10, 3, 1, 1]
              },
              sales_trend: {
                title: t('chatPanel.chart.trendTitle', { defaultValue: 'Sales trend' }),
                labels: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag'],
                data: [42, 39, 47, 54, 0],
                subtitle: t('chatPanel.chart.trendSubtitle', { defaultValue: 'Monthly overview of sales' })
              }
            };
            
            // Add example analysis type for testing
            if (!insightsData.chart_type) {
              insightsData.chart_type = {
                market_overview: true,
                transaction_analysis: true
              };
            }
            
            // Add metadata if missing
            if (!insightsData.analysis_metadata) {
            insightsData.analysis_metadata = {
              total_cars_analyzed: 101,
              date_range: t('chatPanel.chart.defaultDateRange', { defaultValue: 'Last period' })
            };
            }
          }
          
          // Remove the JSON data from the displayed message
          const cleanedText = text.replace(carInsightsMatch[0], '');
          
          // Store the insights data for use in the component
          if (!messages.find(m => m.id === messages[messages.length - 1]?.id)?.carInsightsData) {
            // Only modify if the carInsightsData is not already set
            const lastMessageIndex = messages.length - 1;
            if (lastMessageIndex >= 0 && messages[lastMessageIndex].sender === 'server') {
              // Update the last message with the insights data and the car_insights_analysis tool
              const updatedMessage = {
                ...messages[lastMessageIndex],
                text: cleanedText.trim(),
                carInsightsData: insightsData,
                tool: [...(Array.isArray(messages[lastMessageIndex].tool) ? messages[lastMessageIndex].tool : []), 'car_insights_analysis']
              };
            }
          }
          
          return cleanedText.trim();
        } 
      } catch (error) {
        console.error('Error parsing car insights data:', error);
      }
    }
    
    // Remove file references like @api.js @websocket.js that appear at the end
    return text.replace(/\s?@[^\s]+\.(?:js|jsx|ts|tsx|css|html)\s?/g, '');
  }, [messages]);

  // Track previous loading state to detect when messages finish loading
  const prevLoadingRef = useRef(isLoading);
  
  // Pre-process messages to extract car insights data
  const processedMessages = useCallback(() => {
    return messages.map((msg) => {
      // Don't modify messages that already have car_stock_search or vehicle_details in their tool array
      if (msg.tool && 
          ((Array.isArray(msg.tool) && (
            msg.tool.includes('car_stock_search') || 
            msg.tool.includes('car_configurator') ||
            msg.tool.includes('vehicle_details')
          )) || 
           msg.tool === 'car_stock_search' ||
           msg.tool === 'car_configurator' ||
           msg.tool === 'vehicle_details')) {
        return msg;
      }
      
      if (msg.sender === 'server' && msg.text && !msg.carInsightsData) {
        // Check if this message uses the conversation tool type - if so, skip chart analysis
        const isConversationTool = msg.tool && 
          ((Array.isArray(msg.tool) && msg.tool.includes('conversation')) || 
           msg.tool === 'conversation');
        const toolList = Array.isArray(msg.tool)
          ? msg.tool
          : (msg.tool ? [msg.tool] : []);
        const hasInsightsTool = toolList.includes('car_insights_analysis');
        const hasExplicitSalesSnapshot = (
          /venduto\s+(\d+)\s+auto/i.test(msg.text) &&
          /(totale|disponibili)/i.test(msg.text)
        );
        
        // Build charts only for explicit analytics outputs, not for generic conversational replies.
        if (!isConversationTool && (hasInsightsTool || hasExplicitSalesSnapshot)) {
          
          // Create a synthetic car insights data object for this sales analysis
          // This will make the visualization work even if the backend didn't provide JSON
          const salesText = msg.text;
          
          // Extract key metrics from the text using regex patterns
          const carsMatch = salesText.match(/(?:Abbiamo\s+venduto|venduto)\s+(\d+)\s+auto/i);
          const totalCarsMatch = salesText.match(/su\s+un\s+totale\s+di\s+(\d+)/i);
          const remainingMatch = salesText.match(/(\d+)\s+ancora\s+disponibili/i);
          
          // Extract brand data if available
          const brandMatches = [...salesText.matchAll(/([A-Za-z]+)\s+\((\d+)(?:\s+vendute)?\)/g)];
          const brands = brandMatches.map(match => ({ brand: match[1], count: parseInt(match[2]) }));
          
          // Extract model data if available
          const modelRegex = /([\w\s-]+)\s+\((\d+)\)/g;
          const modelSection = salesText.split('I modelli top sono stati:')[1]?.split('.')[0];
          const modelMatches = modelSection ? [...modelSection.matchAll(modelRegex)] : [];
          const models = modelMatches.map(match => ({ model: match[1].trim(), count: parseInt(match[2]) }));
          
          // Extract body types if available
          const bodyRegex = /(SUV|berlina|wagon|coupé|city car)\s+\((\d+)\)/g;
          const bodyMatches = [...salesText.matchAll(bodyRegex)];
          const bodyTypes = bodyMatches.map(match => ({ type: match[1], count: parseInt(match[2]) }));
          
          // Create synthetic chart data
          const soldCars = carsMatch ? parseInt(carsMatch[1]) : 0;
          const totalCars = totalCarsMatch ? parseInt(totalCarsMatch[1]) : 0;
          const remainingCars = remainingMatch ? parseInt(remainingMatch[1]) : 0;
          
          // Format the data for chart visualization
          const syntheticData = {
            descriptive_information: t('chatPanel.chart.description', { defaultValue: 'Sales insights analysis' }),
            data: {
              sold: soldCars,
              total: totalCars,
              remaining: remainingCars,
              brands: brands,
              models: models,
              bodyTypes: bodyTypes
            },
            chart_type: {
              market_overview: true,
              transaction_analysis: true
            },
            chart_data: {
              distribution: {
                title: t('chatPanel.chart.distributionTitle', { defaultValue: 'Sales distribution by brand' }),
                labels: brands.map(b => b.brand),
                data: brands.map(b => b.count)
              },
              overall_body_styles: {
                title: t('chatPanel.chart.bodyStylesTitle', { defaultValue: 'Sales by body type' }),
                labels: bodyTypes.map(b => b.type),
                data: bodyTypes.map(b => b.count)
              },
              sales_status: {
                title: t('chatPanel.chart.statusTitle', { defaultValue: 'Sales status' }),
                labels: [
                  t('chatPanel.chart.salesStatus.labels.sold', { defaultValue: 'Sold' }),
                  t('chatPanel.chart.salesStatus.labels.available', { defaultValue: 'Available' })
                ],
                data: [soldCars, remainingCars]
              }
            },
            analysis_metadata: {
              total_cars_analyzed: totalCars,
              analysis_type: "transaction_analysis",
              date_range: salesText.match(/per il mese di ([A-Za-z]+)/i)?.[1] || t('chatPanel.chart.defaultDateRange', { defaultValue: 'Last period' })
            }
          };
          
          // Return a new message object with the synthetic car insights data
          return {
            ...msg,
            carInsightsData: syntheticData,
            tool: [...(Array.isArray(msg.tool) ? msg.tool : []), 'car_insights_analysis']
          };
        }
        
        // Skip JSON processing for conversation messages
        if (!isConversationTool) {
          // Check for JSON in code blocks
          const carInsightsMatch = msg.text.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
          if (carInsightsMatch) {
            try {
              // Parse the JSON data
              const insightsData = JSON.parse(carInsightsMatch[1]);
              
              // Check if it has the expected structure for car insights
              if (insightsData.descriptive_information && insightsData.chart_data) {
                // Remove the JSON data from the displayed message
                const cleanedText = msg.text.replace(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/, '').trim();
                
                // Return a new message object with the car insights data
                return {
                  ...msg,
                  text: cleanedText,
                  carInsightsData: insightsData,
                  tool: [...(Array.isArray(msg.tool) ? msg.tool : []), 'car_insights_analysis']
                };
              }
            } catch (error) {
              console.error('Error parsing car insights data:', error);
            }
          }
        }
      }
      return msg;
    });
  }, [messages]);
  
  // Scroll to the bottom when new messages arrive or are sent
  useEffect(() => {
    if (chatContainerRef.current && messages.length > 0) {
      const shouldScrollToBottom = () => {
        const container = chatContainerRef.current;
        // Auto-scroll if already at bottom or close to it
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        // Always scroll if the last message is from the user
        const lastMessage = messages[messages.length - 1];
        const isUserLastMessage = lastMessage && lastMessage.sender === 'user';
        
        return isNearBottom || isUserLastMessage || messages.length <= 3;
      };
      
      if (shouldScrollToBottom()) {
        const scrollToBottom = () => {
          const scrollOptions = {
            top: chatContainerRef.current.scrollHeight,
            behavior: 'smooth',
          };
          chatContainerRef.current.scrollTo(scrollOptions);
        };
        
        // Add a slight delay to ensure any rendering is complete
        const scrollTimer = setTimeout(scrollToBottom, 100);
        
        // Create a highlight effect on the latest message
        const lastMessageId = `message-${messages.length - 1}`;
        const messageElement = document.getElementById(lastMessageId);
        if (messageElement) {
          messageElement.classList.add('highlight-message');
          setTimeout(() => {
            messageElement.classList.remove('highlight-message');
          }, 1500);
        }
        
        return () => clearTimeout(scrollTimer);
      }
    }
  }, [messages, isLoading]);

  // Handle the transition from loading to complete for AI messages
  useEffect(() => {
    // Check if loading state changed from true to false (message finished loading)
    if (prevLoadingRef.current && !isLoading) {
      // AI just finished generating a response
      const scrollToLatestAIMessage = () => {
        if (!chatContainerRef.current || messages.length === 0) return;

        // Find the index of the last AI message
        const lastAIMessageIndex = [...messages].reverse().findIndex(msg => msg.sender === 'server');
        
        if (lastAIMessageIndex >= 0) {
          const actualIndex = messages.length - 1 - lastAIMessageIndex;
          const messageId = `message-${actualIndex}`;
          const messageElement = document.getElementById(messageId);
          
          if (messageElement) {
            // Scroll to the message
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            
            // Highlight the message
            messageElement.classList.add('highlight-message');
            setTimeout(() => {
              messageElement.classList.remove('highlight-message');
            }, 1500);
          }
        }
      };
      
      // Short delay to ensure the DOM has updated with the new message
      setTimeout(scrollToLatestAIMessage, 150);
    }
    
    // Update the previous loading state reference
    prevLoadingRef.current = isLoading;
  }, [isLoading, messages]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      const scrollOptions = {
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      };
      chatContainerRef.current.scrollTo(scrollOptions);
    }
  }, [chatContainerRef]);

  return (
    <>
      {/* Use the ScrollableChatContainer wrapper */}
      <ScrollableChatContainer
        chatContainerRef={chatContainerRef}
        messages={messages}
        darkMode={darkMode}
        scrollToBottom={scrollToBottom}
      >
        {/* Content goes inside the wrapper */}
        {messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2,
              opacity: 0.7,
            }}
          >
            <MessageSquare size={40} />
            <Typography variant="h6">{t('chatPanel.newConversationTitle')}</Typography>
            <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: '70%' }}>
              {t('chatPanel.newConversationDescription')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setShowPromptStructure(true)}
              startIcon={<FileText size={16} />}
              sx={{ mt: 2, borderRadius: '20px', textTransform: 'none' }}
            >
              {t('chatPanel.promptButton')}
            </Button>
          </Box>
        ) : (
          <>
            <MessageList 
              messages={processedMessages()} 
              darkMode={darkMode}
              cleanMessageText={cleanMessageText}
              onShowCarDetail={onShowCarDetail}
            />
            
            {/* Add sales report button when sales analysis is available */}
            {messages.some(msg => 
              msg.sender === 'server' && 
              msg.carInsightsData && 
              (Array.isArray(msg.tool) ? 
                msg.tool.includes('car_insights_analysis') : 
                msg.tool === 'car_insights_analysis')
            ) && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 2, 
                mb: 2,
                opacity: 0.9,
                transition: 'opacity 0.3s',
                '&:hover': { opacity: 1 }
              }}>
                <SalesReportButton 
                  messages={messages} 
                  darkMode={darkMode} 
                />
              </Box>
            )}
            
            {showRawMessages && (
              <Box sx={{ 
                mt: 5, 
                p: 2, 
                bgcolor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(240,240,240,0.7)', 
                borderRadius: 2,
                color: darkMode ? 'white' : 'black',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowX: 'auto'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('chatPanel.rawMessagesTitle')}
                </Typography>
                {messages.map((msg, idx) => {
                  const senderLabel = msg.sender === 'user'
                    ? t('chatPanel.messageLabel.user')
                    : t('chatPanel.messageLabel.assistant');
                  return (
                    <Box key={idx} sx={{ 
                      mb: 3, 
                      p: 1, 
                      borderLeft: '3px solid',
                      borderColor: msg.sender === 'user' ? 'primary.main' : 'secondary.main'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                        {t('chatPanel.messageLabelTemplate', { sender: senderLabel, index: idx + 1 })}
                      </Typography>
                      <pre style={{ margin: 0, fontSize: '0.7rem' }}>
                        {JSON.stringify(msg, null, 2)}
                      </pre>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
        
        {/* Connection error banner moved to parent header area to avoid duplication */}
      </ScrollableChatContainer>

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            py: 1.25,
            color: darkMode ? 'rgba(226,232,240,0.8)' : 'rgba(71,85,105,0.85)',
            fontSize: '0.78rem',
            letterSpacing: '0.03em'
          }}
        >
          <Box
                sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: darkMode
                ? 'linear-gradient(135deg, rgba(14,165,233,0.85), rgba(34,197,94,0.85))'
                : 'linear-gradient(135deg, rgba(3,105,161,0.3), rgba(22,163,74,0.32))',
              boxShadow: darkMode
                ? '0 0 12px rgba(14,165,233,0.35)'
                : '0 0 10px rgba(34,197,94,0.22)'
                }}
              >
            <CircularProgress size={10} thickness={4} sx={{ color: '#fff' }} />
          </Box>
          <Typography component="span" sx={{ fontSize: 'inherit' }}>
            {t('chatPanel.typing')}
          </Typography>
          </Box>
        )}

      {/* Use the completely separate input component */}
      <ChatInput 
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        darkMode={darkMode}
        fileUploadProgress={fileUploadProgress}
        quickActions={toolQuickActions}
        showQuickActions={messages.length === 0}
      />
    </>
  );
});

export default ChatPanel;
